import Vue from "vue";
import {
    fetchProjectDetail,
    updateProject,
    deleteMembers,
    addMembers,
    updateMemberType,
    deleteProject,
    withdrawProject,
    createTestSpace,
    fetchTestSpace
} from "@/api";
import router from "@/router/index";
import { DEVELOPER_PROJECTS } from "@/router/path";
import { randomId } from "@/utils/filters";
import i18n from "@/i18n";

export const state = {
    projectDetail: {},
    testSpace: {},
    temp: {},
    candidates: [],
    deleteList: [],
    addList: [],
    approvalList: []
};

export const getters = {
    projectDetail: ({ projectDetail }) => projectDetail,
    projectId: ({ projectDetail }) => projectDetail.id,
    projectName: ({ projectDetail }) => projectDetail.name ?? "",
    candidates: ({ candidates }) => candidates,
    adminCandidates: ({ candidates }) =>
        candidates.filter(({ status }) => status === "NORMAL"),
    deleteList: ({ deleteList }) => deleteList,
    addList: ({ addList }) => addList,
    approvalList: ({ approvalList }) => approvalList,
    modified: (
        { addList, approvalList, deleteList },
        { projectNameModified }
    ) =>
        !!(
            projectNameModified ||
            addList.length ||
            approvalList.length ||
            deleteList.length
        ),
    projectNameModified: ({ temp, projectDetail }) => {
        if (temp.name === "") return true;
        return temp.name && temp.name !== projectDetail.name;
    },
    hasTestSpaceLoggedIn: ({ testSpace }) => !!testSpace.lastLoginTime
};

export const actions = {
    async FETCH_DEVELOPER_PROJECT_DETAIL(
        { commit, dispatch, getters: { myProjectIds } },
        projectId
    ) {
        try {
            if (
                myProjectIds.length > 0 &&
                !myProjectIds.includes(Number(projectId))
            ) {
                router.replace(DEVELOPER_PROJECTS);
                throw "IS_NOT_MY_PROJECTS";
            }
            const { data } = await fetchProjectDetail(projectId);
            if (data.spaceAdminId) {
                await dispatch("FETCH_PROJECT_TEST_SPACE", projectId);
            }
            commit("SET_DEVELOPER_PROJECT_DETAIL", data);
            dispatch("RESET_PROJECT_DETAIL");
        } catch (e) {
            //
        }
    },
    async FETCH_PROJECT_TEST_SPACE({ commit }, projectId) {
        try {
            const { data } = await fetchTestSpace(projectId);
            commit("SET_PROJECT_TEST_SPACE", data);
        } catch (e) {
            // 사용자에게 오류 노출하지 않음
        }
    },
    async UPDATE_PROJECT_NAME({
        state: { temp: { name } },
        getters: { projectId, projectNameModified }
    }) {
        if (!projectNameModified) return;
        try {
            await updateProject(projectId, { name });
        } catch (e) {
            //
        }
    },
    async UPDATE_SPACE_USE_AGREEMENT(
        { commit, dispatch, getters: { projectId: id } },
        spaceUseAgreement
    ) {
        try {
            const { data } = await updateProject(id, { spaceUseAgreement });
            commit("SET_DEVELOPER_PROJECT_DETAIL", data);
        } catch (e) {
            dispatch("UPDATE_ERROR_SNACKBAR", e);
        }
    },
    async CREATE_TEST_SPACE({ commit, dispatch, getters: { projectId } }) {
        try {
            const { data } = await createTestSpace(projectId);
            commit("PATCH_DEVELOPER_PROJECT_DETAIL", {
                key: "spaceAdminId",
                value: data.adminId
            });
        } catch (e) {
            dispatch("UPDATE_ERROR_SNACKBAR", e);
        }
    },
    async DELETE_MEMBERS({ getters: { projectDetail, deleteList } }) {
        if (!deleteList.length) return;
        try {
            const { id: projectId } = projectDetail;
            const params = { ids: deleteList.join() };
            await deleteMembers(projectId, params);
        } catch (e) {
            //
        }
    },
    async ADD_MEMBERS({ getters: { projectDetail, addList } }) {
        if (!addList.length) return;
        try {
            const { id: projectId } = projectDetail;
            const data = addList.map(email => ({ email }));
            await addMembers(projectId, { type: "REGISTRATION" }, data);
        } catch (e) {
            //
        }
    },
    async APPROVE_MEMBERS({ getters: { projectDetail, approvalList } }) {
        if (!approvalList.length) return;
        try {
            const { id: projectId } = projectDetail;
            const data = approvalList.map(id => ({ id }));
            await updateMemberType(projectId, { type: "APPROVAL" }, data);
        } catch (e) {
            //
        }
    },
    CHANGE_ADMIN({ getters, dispatch }) {
        const members = getters.adminCandidates.map(({ email, id }) => ({
            label: email,
            value: id
        }));
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("changeAdmin.title"),
            slot: () =>
                import("@/components/dialogs/project/Dialog.changeAdmin.vue"),
            admin: null,
            members,
            actions: [
                {
                    action: "cancel",
                    label: i18n.t("label.cancel"),
                    autofocus: true
                },
                { async_action: "confirm", label: i18n.t("label.confirm") }
            ],
            confirm: async dialog => {
                try {
                    const id = getters.dialog.admin;
                    if (id) {
                        const { id: projectId } = getters.projectDetail;
                        await updateMemberType(projectId, { type: "ADMIN" }, [{ id }]);
                        router.push(DEVELOPER_PROJECTS);
                    }
                } catch (e) {
                    // TBD 공통 오류 메세지 처리
                }
                dialog.close();
            }
        });
    },
    async SAVE_CHANGE({ state, getters, dispatch }) {
        // 변경 내용 체크
        const modified = await dispatch("CHECK_IS_MODIFIED");
        if (!modified) return dispatch("RESET_PROJECT_DETAIL");

        const {
            projectDetail: { id: projectId },
            projectByName,
            projectNameModified
        } = getters;

        // 프로젝트명 중복 체크
        if (projectNameModified) {
            const { temp: { name: newProjectName } } = state;
            const duplicated = projectByName(newProjectName);
            if (duplicated) throw new Error("DUPLICATED_LABEL_NAME");
        }

        try {
            await Promise.all([
                dispatch("UPDATE_PROJECT_NAME"),
                dispatch("DELETE_MEMBERS"),
                dispatch("ADD_MEMBERS"),
                dispatch("APPROVE_MEMBERS")
            ]);
            dispatch("UPDATE_SNACKBAR", {
                message: i18n.t("project.updateInfo.save")
            });
        } catch (e) {
            //
        } finally {
            if (projectNameModified) {
                dispatch("FETCH_PROJECTS");
                dispatch("FETCH_MY_PROJECTS");
            }
            dispatch("FETCH_DEVELOPER_PROJECT_DETAIL", projectId);
        }
    },
    async CANCEL_CHANGE({ dispatch }) {
        const modified = await dispatch("CHECK_IS_MODIFIED");
        if (!modified) return dispatch("RESET_PROJECT_DETAIL");

        const action = await dispatch("UPDATE_DIALOG", {
            ...i18n.t("project.updateInfo.cancel"),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        if (action === "confirm") {
            dispatch("RESET_PROJECT_DETAIL");
        }
    },
    DELETE_PROJECT({ getters: { projectDetail, appsByProjectId }, dispatch }) {
        const { id: projectId } = projectDetail;
        const undeletable =
            projectDetail.members.length > 1 ||
            !!appsByProjectId(projectId).length;
        if (undeletable) return dispatch("CONFIRM_PROJECT_UNDELETABLE");
        return dispatch("CONFIRM_DELETE_PROJECT");
    },
    CONFIRM_PROJECT_UNDELETABLE({ dispatch }) {
        return dispatch("UPDATE_DIALOG", {
            title: i18n.t("deleteProject.title"),
            content: i18n.t("deleteProject.undeletable"),
            actions: [{ action: "confirm", label: i18n.t("label.confirm") }]
        });
    },
    async CONFIRM_DELETE_PROJECT({ getters: { projectDetail }, dispatch }) {
        const { name: projectName, id: projectId } = projectDetail;
        const action = await dispatch("UPDATE_DIALOG", {
            title: i18n.t("deleteProject.title"),
            content: i18n.t("deleteProject.content", { projectName }),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        if (action === "cancel") return;
        try {
            await deleteProject(projectId);
            router.replace(DEVELOPER_PROJECTS);
        } catch (e) {
            //
        }
    },
    async WITHDRAW_PROJECT({ getters: { projectDetail }, dispatch }) {
        const { name: projectName, id: projectId } = projectDetail;
        const action = await dispatch("UPDATE_DIALOG", {
            title: i18n.t("withdrawProject.title"),
            content: i18n.t("withdrawProject.content", { projectName }),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                {
                    action: "confirm",
                    label: i18n.t("label.delete"),
                    color: "danger"
                }
            ]
        });
        if (action === "cancel") return;
        try {
            await withdrawProject(projectId);
            router.replace(DEVELOPER_PROJECTS);
        } catch (e) {
            //
        }
    },
    async CONFIRM_LEAVING({ dispatch }) {
        const modified = await dispatch("CHECK_IS_MODIFIED");
        if (!modified) return true;
        const action = await dispatch("UPDATE_DIALOG", {
            ...i18n.t("project.updateInfo.cancel"),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        return action === "confirm";
    },
    CHECK_IS_MODIFIED({ commit, getters }) {
        commit("CLASSIFY_MEMBER_REGISTRATION");
        return getters.modified;
    },
    RESET_PROJECT_DETAIL({ commit }) {
        commit("RESET_CANDIDATES");
        commit("RESET_TEMP_PROJECT_DETAIL");
        commit("RESET_DELETE_LIST");
    },
    DELETE_CANDIDATE({ commit }, { id, exist }) {
        commit("ADD_DELETE_LIST", { id, exist });
        commit("REMOVE_CANDIDATE", id);
    }
};

export const mutations = {
    SET_DEVELOPER_PROJECT_DETAIL(state, value) {
        state.projectDetail = value;
    },
    SET_PROJECT_TEST_SPACE(state, value) {
        state.testSpace = value;
    },
    RESET_DEVELOPER_PROJECT_DETAIL(state) {
        state.projectDetail = {};
        state.testSpace = {};
    },
    PATCH_DEVELOPER_PROJECT_DETAIL(state, { key, value }) {
        Vue.set(state.projectDetail, key, value);
    },
    PATCH_TEMP_PROJECT_DETAIL({ temp }, { key, value }) {
        Vue.set(temp, key, value);
    },
    RESET_TEMP_PROJECT_DETAIL(state) {
        state.temp = {};
    },
    ADD_DELETE_LIST(state, { id, exist }) {
        if (exist) state.deleteList.push(id);
    },
    RESET_DELETE_LIST(state) {
        state.deleteList = [];
    },
    RESET_CANDIDATES(state) {
        const { projectDetail: { members } } = state;
        state.candidates = members
            ? members.reduce((acc, { id, email, role, status }) => {
                if (role === "ADMIN") return acc;
                acc.push({
                    id,
                    email,
                    status, // API 추가용
                    isWaiting: status === "WAIT_APPROVAL", // 화면용
                    exist: true
                });
                return acc;
            }, [])
            : [];
        state.approvalList = [];
        state.addList = [];
    },
    ADD_EMPTY_CANDIDATE(state) {
        state.candidates.push({
            id: randomId(),
            email: null,
            status: "WAIT_REGISTRATION",
            isWaiting: false,
            exist: false
        });
    },
    UPDATE_CANDIDATE({ candidates }, { id, key, value }) {
        const index = candidates.findIndex(item => item.id === id);
        if (index === -1) return;
        candidates.splice(index, 1, { ...candidates[index], [key]: value });
    },
    REMOVE_CANDIDATE({ candidates }, id) {
        const index = candidates.findIndex(item => item.id === id);
        if (index === -1) return;
        candidates.splice(index, 1);
    },
    CLASSIFY_MEMBER_REGISTRATION(state) {
        const { projectDetail, candidates } = state;
        const approvalList = [];
        const addList = new Set();

        candidates.forEach(({ id, email, isWaiting, status }) => {
            if (status === "WAIT_APPROVAL") {
                if (!isWaiting) approvalList.push(id);
            } else {
                if (!email) return;
                if (
                    projectDetail.members.find(item => item.email === email)
                ) {
                    return;
                }
                addList.add(email);
            }
        });
        state.approvalList = approvalList;
        state.addList = [...addList];
    }
};
